import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Receipt, Plus, Filter, CheckCircle2, XCircle, DollarSign,
  Upload, Trash2, ImageIcon, FileText, Eye, ArrowRight, X,
} from "lucide-react";

const STATUS_META = {
  pending: { label: "Pending", cls: "bg-[var(--peak-muted)] text-white" },
  approved: { label: "Approved", cls: "bg-[var(--peak-blue-deep)] text-white" },
  reimbursed: { label: "Reimbursed", cls: "bg-[#008A27] text-white" },
  rejected: { label: "Rejected", cls: "bg-[#E50000] text-white" },
};

const SCOPES = [
  { key: "mine", label: "My expenses" },
  { key: "pending", label: "Pending review" },
  { key: "all", label: "All" },
];

export default function ExpensesPage() {
  const { user } = useAuth();
  const isAdmin = ["owner", "admin"].includes(user?.workspace_role) || user?.is_superadmin;
  const [scope, setScope] = useState(isAdmin ? "pending" : "mine");
  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [count, setCount] = useState(0);
  const [categories, setCategories] = useState({ defaults: [], custom: [], all: [], payment_methods: [] });
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);   // full expense w/ attachments
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: cats }] = await Promise.all([
        api.get(`/expenses`, { params: { scope } }),
        api.get(`/expenses/categories`),
      ]);
      setExpenses(data.expenses || []);
      setTotal(data.total_amount || 0);
      setCount(data.count || 0);
      setCategories(cats);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [scope]);

  // Workspace budget (owner/admin sets, everyone sees the burn bar)
  const [budget, setBudget] = useState({ expense_budget_monthly: null, expenses_this_month: 0, month: "" });
  const loadBudget = useCallback(async () => {
    if (!user?.active_workspace_id) return;
    try {
      const { data } = await api.get(`/workspaces/${user.active_workspace_id}/expense-budget`);
      setBudget(data);
    } catch { /* silent */ }
  }, [user?.active_workspace_id]);
  useEffect(() => { loadBudget(); }, [loadBudget]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/expenses/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doApprove = async (id) => {
    try { await api.post(`/expenses/${id}/approve`); toast.success("Approved"); setDetail(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doReject = async (id, reason) => {
    try { await api.post(`/expenses/${id}/reject`, { reason }); toast.success("Rejected"); setDetail(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doReimburse = async (id, payment_method) => {
    try { await api.post(`/expenses/${id}/reimburse`, { payment_method }); toast.success("Marked reimbursed"); setDetail(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doDelete = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Deleted"); setDetail(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="expenses-page">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="section-label text-[var(--peak-accent)]">REIMBURSEMENTS</div>
          <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tighter mt-1">EXPENSES</h1>
          <p className="text-[var(--peak-muted)] mt-2">
            Submit receipts. Owner approves. Get paid back. Everything is scoped to your active workspace.
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/expenses/reports"
            data-testid="expenses-reports-link"
            className="border-2 border-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-white font-bold uppercase tracking-widest px-4 py-3 text-xs inline-flex items-center gap-2"
          >
            Reports <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <button
            data-testid="new-expense-btn"
            onClick={() => setCreating(true)}
            className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase tracking-widest px-4 py-3 text-xs inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New expense
          </button>
        </div>
      </header>

      <BudgetBanner
        budget={budget}
        isAdmin={isAdmin}
        workspaceId={user?.active_workspace_id}
        onChanged={loadBudget}
      />

      {/* Scope tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-6 border-b border-[var(--peak-border)]">
        {SCOPES.map((s) => (
          <button
            key={s.key}
            onClick={() => setScope(s.key)}
            data-testid={`scope-${s.key}`}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-widest border-b-2 -mb-px transition-colors ${
              scope === s.key ? "border-[var(--peak-accent)] text-[var(--peak-ink)]" : "border-transparent text-[var(--peak-muted)] hover:text-[var(--peak-ink)]"
            }`}
          >
            {s.label}
          </button>
        ))}
        <div className="ml-auto flex gap-4 text-xs">
          <span className="section-label">Rows: <b className="text-[var(--peak-ink)] font-heading">{count}</b></span>
          <span className="section-label">Total: <b className="text-[var(--peak-ink)] font-heading">${total.toFixed(2)}</b></span>
        </div>
      </div>

      {/* List */}
      <div className="border-2 border-[var(--peak-ink)] bg-white overflow-x-auto" data-testid="expenses-table">
        <table className="w-full text-sm">
          <thead className="bg-[var(--peak-bg)] border-b-2 border-[var(--peak-ink)]">
            <tr className="text-left">
              <Th>Date</Th><Th>Submitter</Th><Th>Category</Th><Th>Merchant</Th>
              <Th className="text-right">Amount</Th><Th>Status</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--peak-muted)] text-xs">Loading…</td></tr>
            ) : expenses.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center">
                <Receipt className="w-6 h-6 mx-auto text-[var(--peak-muted)] mb-2" />
                <div className="section-label">No expenses yet</div>
                <div className="text-xs text-[var(--peak-muted)] mt-1">Click "New expense" to submit your first receipt.</div>
              </td></tr>
            ) : expenses.map((e) => {
              const st = STATUS_META[e.status] || STATUS_META.pending;
              return (
                <tr key={e.id} className="border-b border-[var(--peak-border)] hover:bg-[var(--peak-bg)]" data-testid={`expense-row-${e.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{(e.date || "").slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <div className="text-xs font-semibold">{e.submitted_by_name || e.submitted_by_email}</div>
                    <div className="text-[10px] text-[var(--peak-muted)] font-mono">{e.submitted_by_email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs uppercase tracking-wider">{e.category}</td>
                  <td className="px-4 py-3 text-xs truncate max-w-[220px]">{e.merchant || "—"}</td>
                  <td className="px-4 py-3 text-right font-heading font-black">${Number(e.amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openDetail(e.id)}
                      data-testid={`expense-open-${e.id}`}
                      className="text-[10px] font-bold uppercase tracking-widest text-[var(--peak-blue-deep)] hover:text-[var(--peak-accent)] inline-flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" /> Open
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {creating && (
        <NewExpenseModal
          categories={categories}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}

      {detail && (
        <ExpenseDetailModal
          expense={detail}
          isAdmin={isAdmin}
          currentUserId={user?.id}
          paymentMethods={categories.payment_methods}
          onClose={() => setDetail(null)}
          onApprove={() => doApprove(detail.id)}
          onReject={(reason) => doReject(detail.id, reason)}
          onReimburse={(method) => doReimburse(detail.id, method)}
          onDelete={() => doDelete(detail.id)}
        />
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[var(--peak-muted)] ${className}`}>{children}</th>;
}

// ─────────────────────────────────────────────────────────
// New expense modal
// ─────────────────────────────────────────────────────────

function NewExpenseModal({ categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    amount: "",
    currency: "USD",
    category: categories.all?.[0] || "Other",
    date: new Date().toISOString().slice(0, 10),
    merchant: "",
    notes: "",
    attachments: [],
  });
  const [busy, setBusy] = useState(false);

  const onFile = async (files) => {
    const list = Array.from(files || []).slice(0, 8 - form.attachments.length);
    const readers = list.map((f) => new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve({ name: f.name, mime: f.type || "application/octet-stream", data_url: r.result });
      r.onerror = reject;
      r.readAsDataURL(f);
    }));
    try {
      const results = await Promise.all(readers);
      setForm((f) => ({ ...f, attachments: [...f.attachments, ...results].slice(0, 8) }));
    } catch { toast.error("Failed to read one of the files"); }
  };

  const removeAttachment = (idx) => setForm((f) => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Enter a positive amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/expenses", { ...form, amount: amt });
      if (data.budget_status?.over) {
        toast.warning(
          `Submitted — but this pushes the workspace ${data.budget_status.pct}% of the $${data.budget_status.budget.toLocaleString()} monthly budget.`,
          { duration: 8000 }
        );
      } else {
        toast.success("Expense submitted for approval");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Submit an expense" onClose={onClose} testId="new-expense-modal">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="section-label">Amount</label>
            <input
              data-testid="expense-amount"
              type="number" min="0.01" step="0.01" required
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="brutalist-input"
              placeholder="42.50"
            />
          </div>
          <div>
            <label className="section-label">Currency</label>
            <input
              data-testid="expense-currency"
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase().slice(0, 3) })}
              className="brutalist-input"
              maxLength={3}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="section-label">Category</label>
            <select
              data-testid="expense-category"
              className="brutalist-input"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {categories.all?.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="section-label">Date</label>
            <input
              data-testid="expense-date"
              type="date"
              className="brutalist-input"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              required
            />
          </div>
        </div>
        <div>
          <label className="section-label">Merchant</label>
          <input
            data-testid="expense-merchant"
            className="brutalist-input"
            value={form.merchant}
            onChange={(e) => setForm({ ...form, merchant: e.target.value })}
            placeholder="e.g. Delta Airlines"
          />
        </div>
        <div>
          <label className="section-label">Notes</label>
          <textarea
            data-testid="expense-notes"
            rows={3}
            className="w-full border border-[var(--peak-border)] p-2 focus:outline-none focus:border-[var(--peak-ink)]"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Reason / context"
          />
        </div>

        {/* Attachments */}
        <div>
          <label className="section-label">Receipts (photos or PDFs, up to 8)</label>
          <label
            className="mt-1 border-2 border-dashed border-[var(--peak-border)] hover:border-[var(--peak-ink)] p-4 flex flex-col items-center justify-center text-xs text-[var(--peak-muted)] cursor-pointer transition-colors"
            data-testid="expense-attach-dropzone"
          >
            <Upload className="w-5 h-5 mb-1" />
            <span>Click or drag files here</span>
            <span className="text-[10px] mt-0.5">JPEG/PNG/HEIC/PDF · max 8 files</span>
            <input
              type="file"
              multiple
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => onFile(e.target.files)}
              data-testid="expense-file-input"
            />
          </label>
          {form.attachments.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2" data-testid="expense-attach-list">
              {form.attachments.map((a, i) => (
                <div key={i} className="border border-[var(--peak-border)] p-2 flex items-start gap-2 min-w-0">
                  {a.mime.startsWith("image/") ? (
                    <img src={a.data_url} alt={a.name} className="w-10 h-10 object-cover flex-none border border-[var(--peak-border)]" />
                  ) : (
                    <FileText className="w-6 h-6 text-[var(--peak-muted)] flex-none" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-mono truncate">{a.name}</div>
                    <div className="text-[9px] text-[var(--peak-muted)]">{a.mime}</div>
                  </div>
                  <button type="button" onClick={() => removeAttachment(i)} className="text-[var(--peak-muted)] hover:text-[#E50000] flex-none">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold uppercase tracking-widest border border-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-white"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="expense-submit"
            className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[var(--peak-accent)] text-white hover:bg-black disabled:opacity-60 inline-flex items-center gap-2"
          >
            <DollarSign className="w-3.5 h-3.5" /> {busy ? "Submitting…" : "Submit"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────
// Detail modal
// ─────────────────────────────────────────────────────────

function ExpenseDetailModal({ expense, isAdmin, currentUserId, paymentMethods, onClose, onApprove, onReject, onReimburse, onDelete }) {
  const [action, setAction] = useState(null); // 'reject' | 'reimburse' | null
  const [rejectReason, setRejectReason] = useState("");
  const [payMethod, setPayMethod] = useState(paymentMethods?.[0] || "Bank transfer");
  const st = STATUS_META[expense.status] || STATUS_META.pending;
  const canApprove = isAdmin && ["pending", "rejected"].includes(expense.status);
  const canReject = isAdmin && expense.status !== "reimbursed" && expense.status !== "rejected";
  const canReimburse = isAdmin && expense.status === "approved";
  const canDelete = (expense.submitted_by_user_id === currentUserId && expense.status === "pending") || isAdmin;

  return (
    <ModalShell title="Expense" onClose={onClose} testId="expense-detail-modal">
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 ${st.cls}`} data-testid="expense-status-pill">{st.label}</span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--peak-muted)]">{expense.category}</span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Amount">
            <span className="font-heading font-black text-2xl tracking-tighter">${Number(expense.amount || 0).toFixed(2)} <span className="text-xs font-mono text-[var(--peak-muted)]">{expense.currency}</span></span>
          </Field>
          <Field label="Date">{(expense.date || "").slice(0, 10)}</Field>
          <Field label="Merchant">{expense.merchant || "—"}</Field>
          <Field label="Submitted by">{expense.submitted_by_name || expense.submitted_by_email}</Field>
        </div>
        {expense.notes && (
          <div>
            <label className="section-label">Notes</label>
            <div className="text-sm whitespace-pre-wrap mt-1 p-2 border border-[var(--peak-border)] bg-[var(--peak-bg)]">{expense.notes}</div>
          </div>
        )}

        {/* Attachments viewer */}
        {expense.attachments?.length > 0 && (
          <div>
            <label className="section-label">Receipts ({expense.attachments.length})</label>
            <div className="grid grid-cols-2 gap-2 mt-2" data-testid="expense-detail-attachments">
              {expense.attachments.map((a, i) => (
                <a
                  key={i}
                  href={a.data_url}
                  target="_blank" rel="noreferrer"
                  className="border border-[var(--peak-border)] p-2 hover:border-[var(--peak-ink)] flex items-center gap-2 min-w-0"
                  data-testid={`expense-attachment-${i}`}
                >
                  {a.mime.startsWith("image/") ? (
                    <img src={a.data_url} alt={a.name} className="w-16 h-16 object-cover flex-none border border-[var(--peak-border)]" />
                  ) : (
                    <FileText className="w-8 h-8 text-[var(--peak-muted)] flex-none" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-mono truncate">{a.name}</div>
                    <div className="text-[10px] text-[var(--peak-muted)]">{a.mime} · click to open</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* State trail */}
        <div className="text-[10px] font-mono text-[var(--peak-muted)] space-y-0.5 pt-2 border-t border-[var(--peak-border)]">
          <div>Submitted {new Date(expense.submitted_at).toLocaleString()}</div>
          {expense.approved_at && <div>Approved {new Date(expense.approved_at).toLocaleString()} by {expense.approved_by_email}</div>}
          {expense.rejected_at && <div>Rejected {new Date(expense.rejected_at).toLocaleString()} by {expense.rejected_by_email}{expense.rejection_reason ? ` — "${expense.rejection_reason}"` : ""}</div>}
          {expense.reimbursed_at && <div>Reimbursed {new Date(expense.reimbursed_at).toLocaleString()} via {expense.payment_method} by {expense.reimbursed_by_email}</div>}
        </div>

        {/* Inline reject form */}
        {action === "reject" && (
          <div className="border-2 border-[var(--peak-ink)] bg-[var(--peak-bg)] p-3" data-testid="reject-form">
            <label className="section-label">Reason (optional)</label>
            <textarea
              rows={2}
              data-testid="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full border border-[var(--peak-border)] p-2 mt-1 text-sm focus:outline-none focus:border-[var(--peak-ink)]"
              placeholder="e.g. Personal expense, not covered by policy"
            />
            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => { setAction(null); setRejectReason(""); }}
                className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest border border-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-white"
              >Cancel</button>
              <button
                onClick={() => { onReject(rejectReason.trim()); setAction(null); setRejectReason(""); }}
                data-testid="reject-confirm"
                className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest bg-[#E50000] hover:bg-black text-white inline-flex items-center gap-1"
              ><XCircle className="w-3 h-3" /> Confirm reject</button>
            </div>
          </div>
        )}

        {/* Inline reimburse form */}
        {action === "reimburse" && (
          <div className="border-2 border-[#008A27] bg-[var(--peak-bg)] p-3" data-testid="reimburse-form">
            <label className="section-label">Payment method</label>
            <select
              data-testid="reimburse-method"
              value={payMethod}
              onChange={(e) => setPayMethod(e.target.value)}
              className="brutalist-input mt-1"
            >
              {paymentMethods.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => setAction(null)}
                className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest border border-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-white"
              >Cancel</button>
              <button
                onClick={() => { onReimburse(payMethod); setAction(null); }}
                data-testid="reimburse-confirm"
                className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest bg-[#008A27] hover:bg-black text-white inline-flex items-center gap-1"
              ><DollarSign className="w-3 h-3" /> Confirm ${Number(expense.amount || 0).toFixed(2)} reimbursement</button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-[var(--peak-border)]">
          {canDelete && (
            <button
              onClick={onDelete}
              data-testid="expense-delete-btn"
              className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest border border-[var(--peak-ink)] hover:bg-[#E50000] hover:border-[#E50000] hover:text-white inline-flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          )}
          {canReject && (
            <button
              onClick={() => setAction("reject")}
              data-testid="expense-reject-btn"
              className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest border border-[var(--peak-ink)] hover:bg-black hover:text-white inline-flex items-center gap-1"
            >
              <XCircle className="w-3 h-3" /> Reject
            </button>
          )}
          {canApprove && (
            <button
              onClick={onApprove}
              data-testid="expense-approve-btn"
              className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest bg-[var(--peak-blue-deep)] hover:bg-black text-white inline-flex items-center gap-1"
            >
              <CheckCircle2 className="w-3 h-3" /> Approve
            </button>
          )}
          {canReimburse && (
            <button
              onClick={() => setAction("reimburse")}
              data-testid="expense-reimburse-btn"
              className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest bg-[#008A27] hover:bg-black text-white inline-flex items-center gap-1"
            >
              <DollarSign className="w-3 h-3" /> Mark reimbursed
            </button>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="section-label">{label}</label>
      <div className="text-sm mt-1">{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Budget banner
// ─────────────────────────────────────────────────────────

function BudgetBanner({ budget, isAdmin, workspaceId, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");

  const hasBudget = budget?.expense_budget_monthly != null && budget.expense_budget_monthly > 0;
  const spent = budget?.expenses_this_month || 0;
  const b = budget?.expense_budget_monthly || 0;
  const pct = hasBudget ? Math.round((spent / b) * 100) : 0;
  const tone = !hasBudget ? { fill: "bg-[var(--peak-border)]", text: "text-[var(--peak-muted)]" }
    : pct >= 100 ? { fill: "bg-[#E50000]", text: "text-[#E50000]" }
    : pct >= 80 ? { fill: "bg-[#F0AD00]", text: "text-[#F0AD00]" }
    : { fill: "bg-[var(--peak-accent)]", text: "text-[var(--peak-ink)]" };

  const save = async () => {
    const v = val.trim() === "" ? null : parseFloat(val);
    if (v !== null && !(Number.isFinite(v) && v >= 0)) {
      toast.error("Enter a positive amount or leave blank to clear");
      return;
    }
    try {
      await api.patch(`/workspaces/${workspaceId}/expense-budget`, { expense_budget_monthly: v });
      toast.success(v == null ? "Budget cleared" : `Monthly budget set to $${v.toLocaleString()}`);
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!hasBudget && !isAdmin) return null;

  return (
    <div className="mb-6 border-2 border-[var(--peak-ink)] bg-white p-4" data-testid="expense-budget-banner">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="section-label">Monthly expense budget · {budget?.month || "—"}</div>
          {hasBudget ? (
            <div className="mt-1 flex items-baseline gap-2 flex-wrap">
              <span className="font-heading font-black text-3xl tracking-tighter">
                ${spent.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </span>
              <span className="text-sm text-[var(--peak-muted)]">of ${b.toLocaleString()}</span>
              <span className={`text-xs font-mono uppercase tracking-widest ${tone.text}`} data-testid="budget-pct">
                {pct}% used
              </span>
            </div>
          ) : (
            <div className="mt-1 text-sm text-[var(--peak-muted)]">
              No budget set — add one so submitters see a burn indicator and the switcher lights up when spend nears the cap.
            </div>
          )}
        </div>
        {isAdmin && !editing && (
          <button
            data-testid="edit-budget-btn"
            onClick={() => { setVal(hasBudget ? String(b) : ""); setEditing(true); }}
            className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest border border-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-white"
          >
            {hasBudget ? "Edit budget" : "Set budget"}
          </button>
        )}
        {editing && (
          <div className="flex items-center gap-2" data-testid="budget-editor">
            <div className="text-xs font-mono text-[var(--peak-muted)]">$</div>
            <input
              autoFocus
              data-testid="budget-input"
              type="number"
              min="0"
              step="1"
              className="brutalist-input w-28"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
              placeholder="e.g. 2000"
            />
            <button
              data-testid="budget-save"
              onClick={save}
              className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest bg-[var(--peak-accent)] text-white hover:bg-black"
            >Save</button>
            <button
              onClick={() => setEditing(false)}
              className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest border border-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-white"
            >Cancel</button>
          </div>
        )}
      </div>
      {hasBudget && (
        <div className="mt-3 h-2 bg-[var(--peak-border)] relative overflow-hidden">
          <div
            className={`h-full ${tone.fill} transition-all`}
            style={{ width: `${Math.min(100, pct)}%` }}
            data-testid="budget-bar"
          />
        </div>
      )}
      {hasBudget && pct >= 80 && pct < 100 && (
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#F0AD00] mt-2">
          ⚠ Approaching monthly cap. Any new submissions will trigger a soft warning.
        </div>
      )}
      {hasBudget && pct >= 100 && (
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#E50000] mt-2">
          ⚠ Over budget by ${(spent - b).toLocaleString(undefined, { maximumFractionDigits: 2 })}. Consider reviewing pending submissions.
        </div>
      )}
    </div>
  );
}

function ModalShell({ title, onClose, testId, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 md:p-8" onMouseDown={onClose}>
      <div
        className="w-full max-w-2xl bg-white border-2 border-[var(--peak-ink)] shadow-[8px_8px_0_0_var(--peak-shadow)] my-8"
        onMouseDown={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-[var(--peak-ink)] bg-[var(--peak-surface-alt)]">
          <div className="font-heading font-black text-2xl tracking-tighter uppercase">{title}</div>
          <button onClick={onClose} className="hover:text-[#E50000]" data-testid="modal-close">
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
